import React, { useEffect, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  RefreshControl,
  Alert,
} from 'react-native';
import { useTheme } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import {
  fetchDashboardInsights,
  fetchDashboardRecommendations,
  refreshDashboard,
  selectDashboardInsights,
  selectDashboardRecommendations,
  selectDashboardLoading,
  selectDashboardError,
  selectIsRefreshing,
  selectPersonalizedGreeting,
  selectNextBestAction,
} from '../../store/slices/smartDashboardSlice';
import { AIGreetingHeader } from './AIGreetingHeader';
import { TodaysFocusCard } from './TodaysFocusCard';
import { ProgressCelebration } from './ProgressCelebration';
import { AdaptiveQuickActions } from './AdaptiveQuickActions';
import { AIRecommendationCard } from './AIRecommendationCard';
import { logger } from '../../utils/logger';
// ErrorBoundary removed - will be handled at app level

interface SmartDashboardProps {
  userId: string;
  onNavigate: (screen: string, params?: any) => void;
}

export function SmartDashboard({ userId, onNavigate }: SmartDashboardProps) {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const insights = useSelector(selectDashboardInsights);
  const recommendations = useSelector(selectDashboardRecommendations);
  const loading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);
  const isRefreshing = useSelector(selectIsRefreshing);
  const personalizedGreeting = useSelector(selectPersonalizedGreeting);
  const nextBestAction = useSelector(selectNextBestAction);

  // Initialize dashboard data
  useEffect(() => {
    if (userId && !insights) {
      dispatch(fetchDashboardInsights(userId));
      dispatch(fetchDashboardRecommendations({ userId, limit: 5 }));
    }
  }, [dispatch, userId, insights]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    if (userId) {
      dispatch(refreshDashboard(userId));
    }
  }, [dispatch, userId]);

  // Handle errors
  useEffect(() => {
    if (error && error.recoverable) {
      Alert.alert('Dashboard Update', error.message, [
        { text: 'Retry', onPress: handleRefresh },
        { text: 'OK', style: 'cancel' },
      ]);
    }
  }, [error, handleRefresh]);

  // Handle navigation actions
  const handleActionPress = useCallback(
    (actionType: string, actionId?: string) => {
      switch (actionType) {
        case 'continue_journey':
          onNavigate('Journey');
          break;
        case 'try_recipe':
          if (actionId) {
            onNavigate('RecipeDetail', { recipeId: actionId });
          } else {
            onNavigate('Recipes');
          }
          break;
        case 'watch_video':
          if (actionId) {
            onNavigate('VideoDetail', { videoId: actionId });
          } else {
            onNavigate('Videos');
          }
          break;
        case 'take_quiz':
          if (actionId) {
            onNavigate('Quiz', { quizId: actionId });
          } else {
            onNavigate('Quizzes');
          }
          break;
        case 'practice_skill':
          if (actionId) {
            onNavigate('SkillPractice', { skillId: actionId });
          } else {
            onNavigate('Skills');
          }
          break;
        case 'view_favorites':
          onNavigate('Favorites');
          break;
        default:
          logger.warn('Unknown action type:', actionType);
      }
    },
    [onNavigate]
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            colors={[theme.colors.primary]}
            tintColor={theme.colors.primary}
          />
        }
      >
        {/* AI Greeting Header */}
        <AIGreetingHeader
          greeting={personalizedGreeting}
          insights={insights}
          loading={loading.insights}
          onRefresh={handleRefresh}
        />

        {/* Today's Focus Card */}
        {nextBestAction && (
          <TodaysFocusCard
            action={nextBestAction}
            onActionPress={handleActionPress}
            loading={loading.insights}
          />
        )}

        {/* Progress Celebration */}
        {insights && (
          <ProgressCelebration
            insights={insights}
            onViewProgress={() => onNavigate('Progress')}
            onViewAchievements={() => onNavigate('Achievements')}
          />
        )}

        {/* Adaptive Quick Actions */}
        <AdaptiveQuickActions
          insights={insights}
          recommendations={recommendations}
          onActionPress={handleActionPress}
          loading={loading.recommendations}
        />

        {/* AI Recommendations */}
        {recommendations.length > 0 && (
          <View style={styles.recommendationsSection}>
            {recommendations.slice(0, 3).map(recommendation => (
              <AIRecommendationCard
                key={recommendation.id}
                recommendation={recommendation}
                onAccept={() =>
                  handleActionPress('try_recipe', recommendation.contentId)
                }
                onDismiss={() => {
                  // TODO: Implement recommendation dismissal
                  logger.log('Dismiss recommendation:', recommendation.id);
                }}
                onFeedback={feedback => {
                  // TODO: Implement feedback collection
                  logger.log('Recommendation feedback:', feedback);
                }}
              />
            ))}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  recommendationsSection: {
    paddingHorizontal: 16,
    paddingBottom: 24,
    gap: 12,
  },
});
